import { defineStore } from 'pinia';
import { ref } from 'vue';

import { toast } from 'vue-sonner';

import _ from 'lodash';
import type { API_Message } from '@/api/types';

class ValhallaClient {
  private url: string;
  private auth?: () => string;
  private socket: WebSocket | null = null;
  private listeners: ((msg: API_Message) => void)[];
  private state: 'open' | 'closed';
  private connecting: boolean = false;
  private reconnectAttempts: number = 0;
  private reconnectDelay: number = 5000; // Delay between reconnect attempts in ms

  constructor(url: string, auth?: () => string) {
    this.url = url;
    this.listeners = [];
    this.state = 'closed';
    this.auth = auth;
  }

  private onMessage(event: MessageEvent): void {
    for (const h of this.listeners) {
      // Parse the json in data
      let msg: { message: string; content: API_Message };
      try {
        msg = JSON.parse(event.data);
      } catch (error) {
        console.error('[Valhalla] Failed to parse message:', error);
        continue;
      }

      h(msg.content);
    }
  }

  private onClose(): void {
    console.warn('[Valhalla] WebSocket connection closed');
    this.state = 'closed';
    this.socket = null;

    // Attempt to reconnect
    this.reconnectAttempts++;
    console.log(`[Valhalla] Attempting to reconnect (${this.reconnectAttempts})...`);
    setTimeout(() => this.connect(), this.reconnectDelay);
  }

  private onError(event: Event): void {
    console.error('[Valhalla] WebSocket error:', event);
    this.socket?.close(); // Ensure the socket is closed on error
  }

  connect() {
    if (this.state !== 'open' && !this.connecting) {
      this.connecting = true;

      // Create WebSocket connection
      const token = this.auth?.();
      this.socket = new WebSocket(token ? `${this.url}?token=${token}` : this.url);

      // Attach listeners
      this.socket.addEventListener('open', () => {
        console.log('[Valhalla] WebSocket connection opened');
        this.state = 'open';
        this.connecting = false;
        this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
      });

      this.socket.addEventListener('message', this.onMessage.bind(this));
      this.socket.addEventListener('close', this.onClose.bind(this));
      this.socket.addEventListener('error', this.onError.bind(this));
    } else {
      console.warn('[Valhalla] WebSocket is already connected or connecting');
    }
  }

  listen(handler: (msg: API_Message) => void) {
    this.listeners.push(handler);
  }

  send(data: any) {
    if (!this.socket || this.state !== 'open') {
      throw new Error('[Valhalla] Cannot send data: WebSocket is not open');
    }

    this.socket.send(data);
  }
}

export const useValhallaStore = defineStore('valhalla', () => {
  const state = {
    messages: ref<any[]>([]),
  };

  const vc = new ValhallaClient(import.meta.env.VITE_VALHALLA_URL, () => localStorage.getItem('nh-token') ?? '');
  vc.listen((msg) => {
    state.messages.value.push(msg);
    if (!msg.notification) {
      console.debug('[Valhalla] Message does not contain a notification portion:', msg);
      return;
    }

    const title = msg.notification.title;
    const description = msg.notification.description || '-';
    let duration: number | undefined;
    if (msg.notification.persistent) {
      duration = Infinity; // Keep the notification open indefinitely
    } else {
      duration = msg.notification.duration === 0 ? undefined : msg.notification.duration;
    }
    switch (msg.notification.type) {
      case 'success':
        toast.success(title, { description, duration });
        break;
      case 'info':
        toast.info(title, { description, duration });
        break;
      case 'warning':
        toast.warning(title, { description, duration });
        break;
      case 'error':
        toast.error(title, { description, duration });
        break;
      case 'loading':
        // TODO: Implement proper handling of this so that this waits for a "done" message or something
        console.warn("The 'loading' notification type is not implemented");
        toast.loading(title, { description, dismissible: false });
        break;
      case 'done':
        // TODO: See above for 'loading'
        console.warn("The 'done' notification type is not implemented");
        break;
      default:
        toast.message(title, { description, duration });
    }
  });
  vc.connect();

  const actions = {};

  return {
    ...state,
    ...actions,
  };
});
